/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  /** Which numbered step the admin is currently on. */
  activeStep: 1 | 2;
};

const styles = {
  root: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);
    max-width: 415px;
    margin-bottom: var(--cds-spacing-300);
  `,
  step: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-150);
  `,
  circle: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    flex-shrink: 0;
    transition: background-color 0.15s ease;
  `,
  circleActive: css`
    background: var(--cds-color-emphasis-primary-background-xstrong);
    color: var(--cds-color-neutral-background-primary);
  `,
  circleInactive: css`
    background: var(--cds-color-emphasis-primary-background-weak);
    color: var(--cds-color-emphasis-primary-content-default);
  `,
  labelInactive: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  connector: css`
    flex: 1;
    height: 1px;
    border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    min-width: 80px;
  `,
};

/**
 * Two-step indicator rendered above the body on the Define-goal and
 * Review-and-send screens. Mirrors the design mock's `.stepper`: filled
 * circle + bold label for the current step; check mark for completed steps;
 * lighter "inactive" treatment for upcoming steps.
 */
const MessageStepper = ({ activeStep }: Props) => {
  const step1Completed = activeStep > 1;
  const step1Active = activeStep === 1;
  const step2Active = activeStep === 2;

  return (
    <div css={styles.root} role="list" aria-label={_t('Create a message progress')}>
      <div css={styles.step} role="listitem" aria-current={step1Active ? 'step' : undefined}>
        <div
          css={[styles.circle, step1Active || step1Completed ? styles.circleActive : styles.circleInactive]}
          aria-hidden="true"
        >
          {step1Completed ? (
            <CheckIcon size="small" color="invert" />
          ) : (
            <Typography2 component="span" variant="actionPrimary" color={step1Active ? 'invertBody' : 'body'}>
              1
            </Typography2>
          )}
        </div>
        <Typography2
          component="span"
          variant="subtitleMedium"
          css={!step1Active && !step1Completed ? styles.labelInactive : undefined}
        >
          {_t('Define your goal')}
        </Typography2>
      </div>
      <div css={styles.connector} aria-hidden="true" />
      <div css={styles.step} role="listitem" aria-current={step2Active ? 'step' : undefined}>
        <div css={[styles.circle, step2Active ? styles.circleActive : styles.circleInactive]} aria-hidden="true">
          <Typography2 component="span" variant="actionPrimary" color={step2Active ? 'invertBody' : 'body'}>
            2
          </Typography2>
        </div>
        <Typography2 component="span" variant="subtitleMedium" css={step2Active ? undefined : styles.labelInactive}>
          {_t('Review and send')}
        </Typography2>
      </div>
    </div>
  );
};

export default MessageStepper;
